import { FC, useEffect, useRef, useState } from 'react';
import DownArrow from 'assets/Images/Common/down-arrow.png';
import SearchIcon from 'assets/Images/svg-images/input-search.svg';
import { z } from 'zod';
import React from 'react';
import { useOutsideClick } from 'app/hooks/useOutsideClick';

const options = z.object({
  name: z.string(),
  value: z.string(),
});

type option = z.infer<typeof options>;

interface Props {
  options: option[];
  onSelect: (option: string) => void;
  value: string | undefined;
  onSearch?: (value: string) => void;
}

const SearchValueDropdown: FC<Props> = ({ options, onSelect, value, onSearch }) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [dropdownOptions, setDropdownOptions] = useState<Array<option>>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef<boolean>(false);

  useOutsideClick(dropdownRef, () => {
    setShowDropdown(false);
  });

  useEffect(() => {
    setDropdownOptions(options);
  }, [options]);

  useEffect(() => {
    if (showDropdown) {
      searchInputRef.current?.focus();
    }
  }, [showDropdown]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (onSearch) {
      onSearch(searchedValue);
      return;
    }
    let tempOptions: option[] = [...dropdownOptions];
    tempOptions =
      searchedValue && searchedValue?.length > 0
        ? options.filter((option) =>
            option.name.toLowerCase().includes(searchedValue.toLowerCase())
          )
        : options;
    setDropdownOptions(tempOptions);
  }, [searchedValue]);

  return (
    <div ref={dropdownRef}>
      <div
        onClick={() => setShowDropdown((prev) => !prev)}
        className={`${
          showDropdown ? 'rounded-b-none border-primary dark:border-white' : ''
        }  text-sm rounded-md leading-4 text-[#212529] dark:text-white flex items-center justify-between sm:h-[38px] py-2 px-4 focus:outline-none bg-white border border-[#ced4da]  dark:bg-[#41464E]  dark:border-[#fff]`}
        data-pw="dropdown"
      >
        {value}
        <span>
          <img
            className="dark:brightness-0 dark:invert"
            src={DownArrow}
            alt="Arrow"
          />
        </span>
      </div>

      {showDropdown ? (
        <div className="border border-t-0 rounded-b-md border-[#ced4da] dark:border-[#fff] bg-white relative z-50">
          <div className="relative">
            <img
              src={SearchIcon}
              alt="Search"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 dark:invert"
            />
            <input
              ref={searchInputRef}
              className="border-b border-[#ced4da] dark:border-[#fff] dark:bg-[#41464E] h-9 pl-9 pr-4 py-2 text-13 font-medium leading-3 text-gray-700 placeholder:text-gray-400 dark:placeholder:text-[#CED4DA] dark:text-white rounded-t w-full focus:outline-none focus:border-primary dark:focus:border-white"
              type="text"
              name="Search"
              placeholder="Search..."
              value={searchedValue}
              onChange={(e) => setSearchedValue(e.target.value)}
            />
          </div>
          <div className="h-40 overflow-auto scrollbar-hide">
            {dropdownOptions.map((option, index) => (
              <div
                onClick={() => {
                  onSelect(option.value);
                  setShowDropdown(false);
                }}
                className={`${
                  dropdownOptions.length === index + 1 ? 'border-b-0' : ''
                } p-3 text-sm font-medium leading-4 text-[#495057] dark:text-[#CED4DA]  dark:text-white border-b border-dropdownHover dark:border-[#fff] hover:bg-dropdownHover dark:text-white dark:hover:bg-white dark:hover:text-[#495057] dark:text-[#CED4DA]   dark:bg-[#41464E]  dark:text-white`}
                key={option.value}
                data-pw={
                  dropdownOptions.length - 1 === index ? 'last-option' : ''
                }
              >
                {option.name}
              </div>
            ))}
          </div>
        </div>
      ): null }
    </div>
  );
};
export default SearchValueDropdown;
